import { Component, OnInit, AfterContentChecked, Input } from '@angular/core';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { faEye } from '@fortawesome/free-regular-svg-icons';
import { faPrint, faPills, faEdit, faPlusCircle, faMicroscope } from '@fortawesome/free-solid-svg-icons';

import { Appointment } from '../appointment';
import { Patient } from '../../patient/patient';
import { Prescription } from '../../prescription/prescription';
import { Scheduling } from '../../scheduling/scheduling';
import { ExamTemplate } from '../../exam-template/exam-template';
import { ExamRequest } from '../../exam/exam-request';

import { HealthgroupService } from '../../service/healthgroup.service';
import { AppointmentService } from '../../service/appointment.service';
import { PrescriptionService } from '../../service/prescription.service';
import { ExamRequestService } from '../../service/exam-request.service';
import { SchedulingService } from '../../service/scheduling.service';
import { AuthService } from '../../service/auth.service';

import { PrescriptionPrint } from '../../print/prescription.print';
import { SadtPrint } from '../../print/sadt.print';

@Component({
  selector: 'mc-appointment-show',
  templateUrl: './appointment-show.component.html',
  styleUrls: ['./appointment-show.component.scss']
})
export class AppointmentShowComponent implements OnInit, AfterContentChecked {

  eyeIcon = faEye;
  printIcon = faPrint;
  pillsIcon = faPills;
  editIcon = faEdit;
  plusCircle = faPlusCircle;
  microscopeIcon = faMicroscope;

  @Input() appointments: Appointment[];
  @Input() prescriptions: Prescription[];
  @Input() patient: Patient;
  @Input() exams: ExamRequest[];
  private editing: boolean = false;
  private editable: boolean = false;
  private prescribing: boolean = false;
  private requesting: boolean = false;
  private prescription: Prescription;
  private editableAppointment: Appointment;
  private scheduling: Scheduling;
  private exam: ExamRequest;

  constructor(
    private notifService: ToastrService,
    private router: Router,
    private appointmentService: AppointmentService,
    private prescriptionService: PrescriptionService,
    private examRequestService: ExamRequestService,
    private hgService: HealthgroupService,
    private prescriptionPrint: PrescriptionPrint,
    private sadtPrint: SadtPrint,
    private schedulingService: SchedulingService,
    private authService: AuthService
  ) {}

  ngOnInit() {
    this.loadPatientScheduling();
  }

  loadPatientScheduling(): void {
    if (!this.patient) { return; } // TODO: At this time, I don't know why patient is undefined here if navigate back and forth multiple times

    const doctor = this.authService.currentUser;
    this.schedulingService.findByDoctorPatientDate(doctor.id, this.patient.id, new Date()).subscribe(
        (scheduling: Scheduling[]) => {
            if (scheduling && scheduling.length > 0) {
                this.scheduling = scheduling[0];
            }
        },
        error => {
          this.notifService.error(error);
        }
    );
  }

  ngAfterContentChecked() {
    const today = new Date();
    const year = new Intl.DateTimeFormat('en', { year: 'numeric' }).format(today);
    const month = new Intl.DateTimeFormat('en', { month: '2-digit' }).format(today);
    const day = new Intl.DateTimeFormat('en', { day: '2-digit' }).format(today);
    const todayAsString = `${year}${month}${day}`;

    this.editableAppointment = this.appointments.filter(element => {
      // at this moment, element.date is defined as Date, but is a string... :(
      const elementDate = new Date(element.date);
      const elementYear = new Intl.DateTimeFormat('en', { year: 'numeric' }).format(elementDate);
      const elementMonth = new Intl.DateTimeFormat('en', { month: '2-digit' }).format(elementDate);
      const elementDay = new Intl.DateTimeFormat('en', { day: '2-digit' }).format(elementDate);

      this.editable = `${elementYear}${elementMonth}${elementDay}` == todayAsString;
      if (this.editable) return element;
    })[0];
  }

  startEditing(): void {
    this.editing = true;
  }

  onFormCanceled(): void {
    this.editing = false;
  }

  onFormSaved(appointment: Appointment): void {
    if (this.editing && !this.editable) {
      this.editing = false;
      appointment.patient = this.patient;
      this.appointmentService.createAppointment(appointment).subscribe(
        appointment => {
          this.appointments.push(appointment);
          this.editable = true;
          this.editableAppointment = appointment;
        },
        error => {
          this.notifService.error(error);
        }
      );
    } else if (this.editing && this.editable) {
      this.editing = false;
      this.appointmentService.updateAppointment(appointment).subscribe(
        appointment => {
          const currentIndex = this.appointments.indexOf(this.editableAppointment);
          if (currentIndex > -1) {
            this.appointments.splice(currentIndex, 1);
          }
          this.appointments.push(appointment);
          this.editableAppointment = appointment;
          this.notifService.success('Atendimento atualizado');
        },
        error => {
          this.notifService.error(error);
          setTimeout(() => { window.location.reload(); }, 2500);
        }
      );
    }
  }

  startPrescribing(): void {
    this.prescription = new Prescription();
    this.prescription.date = new Date();
    this.prescribing = true;
  }

  onPrescribingCanceled(): void {
    this.prescribing = false;
  }

  onPrescribingSaved(prescription: Prescription): void {
    if (!prescription) { return; }

    this.prescription.patient = this.patient;
    this.prescriptionService.createPrescription(prescription).subscribe(
      result => {
        this.prescriptions.push(result);
        this.prescribing = false;
      },
      error => {
        this.notifService.error(error);
      }
    );
  }

  startRequesting(): void {
    this.exam = new ExamRequest();
    this.exam.exam_templates = [];
    this.exam.date = new Date();
    this.requesting = true;
  }

  onRequestingCanceled(): void {
    this.requesting = false;
  }

  onRequestingSaved(examsRequested: ExamRequest): void {
    if (!examsRequested) { return; }

    this.exam.patient = this.patient;
    this.examRequestService.createExamRequest(this.exam).subscribe(
      result => {
        this.exams.push(result);
        this.requesting = false;
      },
      error => {
        this.notifService.error(error);
      }
    );
  }

  printPrescription(id: number): void {
    const prescription = this.prescriptions.find(p => p.id == id);
    this.prescriptionPrint.execute(this.patient, prescription);
  }

  printSadt(id: number): void {
    if (!id) { return; }

    const examRequest = this.exams.find(e => e.id == id);

    const chunkSize = 5;
    const slicedArray = new Array<ExamTemplate[]>();
    const examTemplates = examRequest.exam_templates;
    for (let i = 0, j = 0; i < examTemplates.length; i += chunkSize, j++) {
      slicedArray[j] = examTemplates.slice(i, i + chunkSize);
    }

    for (let i = 0; i < slicedArray.length; i++) {
      this.examRequestService.getNextSadtNumber().subscribe(
        nextSadtNumber => this.sadtPrint.execute(this.patient, examRequest.root_cause, slicedArray[i], nextSadtNumber),
        error => {
          this.notifService.error(error);
        }
      );
    }
  }

  getPatientAge(): string {
    if ((!this.patient) || (!this.patient.dob)) return '';

    return Patient.getCurrentAge(this.patient);
  }
}
